import { WrapperGS, Valued} from '../core/WrapperGS.js';
import { EventGSHandlerDisposable } from '../core/EventsGS';
import * as array from '../helpers/array';
import * as def from '../helpers/def';
import * as validation from '../helpers/validation';

export interface WrapperValidationFunction {
    (wr: WrapperGS): string | boolean;
}

interface LinkedFormControl {
    wrapper: WrapperGS;
    disposables: EventGSHandlerDisposable[];
}

interface FieldValidationPresentation {
    readonly target: HTMLElement;
    readonly fieldSet?: HTMLFieldSetElement;
    readonly formItem?: WrapperGS;
}

interface FieldValidationResult {
    readonly isValid: boolean;
    readonly errorsText: string;
    readonly hasRules: boolean;
}

class TextValueWrapper implements Valued
{
    private readonly _wr:WrapperGS;
    constructor(wr:WrapperGS)
    {
        this._wr=wr;
    }
    get value(){
        return this._wr.el.innerText;
    }
    set value(value:any){
        this._wr.el.innerText=value+"";
    }
}

export class FormGS extends WrapperGS
{   
    static TagClassName = 'gs-form';
    static ForHTMLElement(el: HTMLElement): boolean { return el.nodeName.toLowerCase() === "form"; }

    /**На данный блок накладывается is-valid is-invalid. Ищется он подъемом вверх по родителям от input*/
    static TagClassItemName = 'gs-form_item';
    private static readonly _ExternalFunc = new Map <string, WrapperValidationFunction>()
    

    static TagClassValid = 'is-valid';
    static TagClassInvalid = 'is-invalid';

    static AddExternalValidation(name: string, func: WrapperValidationFunction) { this._ExternalFunc.set(name, func); }    

    private _handlers: LinkedFormControl[] = [];
    private readonly _validationVersions = new WeakMap<HTMLElement, number>();
    private _validationGeneration = 0;
    private _firstTimeValidated = false;

    constructor(el: HTMLElement|string)
    {
        super(el);        
        this.addEventHandler();
    }

    public async validAsync(): Promise<boolean>
    {
        this.addEventHandler();
        this._firstTimeValidated = true;
        const valids = await array.parallelAsync(this._handlers, (w) => this.validFieldAsync(w.wrapper));
        const valid = valids.filter(r => r === false).length === 0;
        return valid;
    }

    public async fillFormData(data: any, withValidation=true)
    {
        this.addEventHandler();
        for (const control of this._handlers) {
            const w = control.wrapper;
            const name = w.attr('name');
            if (!name) continue;
            if (data[name] === undefined) continue;            
            
            const inputEl = def.toType(w.el, HTMLInputElement);
            if (inputEl) {
                if (inputEl.type === "file") {
                    if (!isFileInputClearValue(data[name]))
                        throw new TypeError(
                            `Cannot fill file input '${name}' with a non-empty value. `
                            + 'Browsers only allow clearing a selected file.'
                        );
                    inputEl.value = '';
                    continue;
                }
                if (inputEl.type === "radio") {
                    inputEl.checked = inputEl.value === def.string(data[name]);
                    continue;
                }
                if (inputEl.type === "checkbox") {
                    if (!Array.isArray(data[name])) continue;
                    const array:string[] = [];
                    (data[name] as any[]).forEach(v => array.push(def.string(v)));
                    inputEl.checked = array.includes(inputEl.value);
                    continue;
                }
            }
            const valuedWr = WrapperGS.ToValued(w);
            if (valuedWr) {
                valuedWr.value = data[name];
                continue;
            }
            const valuedAsyncWr = WrapperGS.ToAsyncValued(w);
            if (valuedAsyncWr) {
                await valuedAsyncWr.setValueAsync(data[name]);
                continue;
            }    
            const valuedText = new TextValueWrapper(w);
            if (valuedText)
            {
                valuedText.value = data[name];
                continue;
            }
        }
        if (withValidation) await this.getFormData();
    }
    public async getFormData(evenNotValid = false): Promise<Record<string, any> | undefined>
    {
        const valid = await this.validAsync();
        if (!evenNotValid)
            if (!valid) return undefined;
        const data: Record<string, any> = {};
        for (const control of this._handlers) {            
            const w = control.wrapper;
            const name = w.attr('name');
            if (!name) continue;
            const inputEl = def.toType(w.el, HTMLInputElement);
            if (inputEl) {
                switch (inputEl.type) {
                    case "file":
                        if (!Array.isArray(data[name])) data[name] = [];
                        data[name].push(...Array.from(inputEl.files ?? []));
                        continue;
                    case "radio":
                        if (inputEl.checked) data[name] = inputEl.value;
                        continue;
                    case "checkbox":
                        if (!data[name]) { data[name] = new Array<string>(0); }   
                        if (!inputEl.checked) continue;                        
                        data[name].push(inputEl.value);
                        continue;
                }
            }
            const valuedWr = WrapperGS.ToValued(w);
            if (valuedWr) {
                data[name] = valuedWr.value;
                continue;
            }
            const valuedAsyncWr = WrapperGS.ToAsyncValued(w);
            if (valuedAsyncWr) {
                data[name] = await valuedAsyncWr.getValueAsync();
                continue;
            }
            const valuedText = new TextValueWrapper(w);
            if (valuedText)
            {
                data[name] = valuedText.value;
                continue;
            }
        }
        return data;
    }

    destroy(): void
    {
        this._validationGeneration++;
        this._handlers.forEach(h => h.disposables.forEach(d => d.dispose()));
        array.clear(this._handlers);
    }


    private async validFieldAsync(field: WrapperGS): Promise<boolean | undefined>
    {
        if (!field.el.isConnected) {
            this.unlinkControl(field);
            return undefined;
        }

        // The first field event validates the complete form, preserving the existing contract.
        if (!this._firstTimeValidated) return this.validAsync();

        const presentation = this.getValidationPresentation(field);
        const version = (this._validationVersions.get(presentation.target) ?? 0) + 1;
        const generation = this._validationGeneration;
        this._validationVersions.set(presentation.target, version);

        const result = await this.evaluateFieldAsync(field);
        if (!field.el.isConnected) {
            this.unlinkControl(field);
            return undefined;
        }
        if (generation !== this._validationGeneration
            || this._validationVersions.get(presentation.target) !== version)
            return result.isValid;

        this.applyValidationResult(presentation, result);
        return result.isValid;
    }

    private getValidationPresentation(field: WrapperGS): FieldValidationPresentation {
        const fieldSet = def.toType(field.el, HTMLFieldSetElement);
        let formItem: WrapperGS | undefined = field;
        while (true) {
            if (formItem.hasClass(FormGS.TagClassItemName)) break;
            if (!formItem.el.parentElement) {
                formItem = undefined;
                break;
            }
            formItem = WrapperGS.Wrap(formItem.el.parentElement);
        }

        return {
            target: formItem?.el ?? field.el,
            fieldSet,
            formItem
        };
    }

    private async evaluateFieldAsync(field: WrapperGS): Promise<FieldValidationResult> {
        const rulesArg = field.attr('validrules');
        if (!rulesArg) return { isValid: true, errorsText: '', hasRules: false };
        const rules = rulesArg.trim().split(/\s+/).filter(Boolean);
        const validErrors: string[] = [];
        const addError = (error: boolean | string) => {
            if (typeof error === "string") { validErrors.push(error); return true; }
            return false;
        };

        for (const ruleRecord of rules) {
            let rule: string;
            let ruleArg = "";

            const separatorIndex = ruleRecord.indexOf("=");
            if (separatorIndex >= 0) {
                rule = ruleRecord.slice(0, separatorIndex).toLowerCase();
                ruleArg = ruleRecord.slice(separatorIndex + 1);
            } else
                rule = ruleRecord.toLowerCase();

            if (rule === 'external')
            {
                if (!FormGS._ExternalFunc.has(ruleArg)) {
                    addError(validation.messages.unknownFunction.replace("{arg0}", ruleArg));
                    continue;
                }
                const validationFunction = FormGS._ExternalFunc.get(ruleArg);
                if (validationFunction) addError(validationFunction(field));
                continue;
            }

            const validable = WrapperGS.ToValidable(field);
            if (validable) { addError(validable.valid(rule, ruleArg)); continue; }
            const validableAsync = WrapperGS.ToAsyncValidable(field);
            if (validableAsync) { addError(await validableAsync.validAsync(rule, ruleArg)); continue; }
            const valued = getValidationValue(field);

            let anotherControl: EventGSHandlerDisposable | undefined;
            const addSameError = (control: EventGSHandlerDisposable | undefined) => {
                if (!control) {
                    addError(validation.messages.notFoundSameField);
                    return;
                }

                const anotherValued = getComparableValidationValue(control.wrapper);
                if (!anotherValued) addError(validation.messages.notFoundSameField);
                else addError(validation.valueSame(valued, anotherValued));
            };
            switch (rule) {
                case 'req':
                    if (inputIsUnselected(field, this._handlers)) addError(validation.messages.noSelectedValue);
                    else addError(validation.valueRequired(valued));
                    break;
                case 'number':
                case 'numbers': addError(validation.valueIsNumber(valued)); break;
                case 'max': addError(validation.valueNumberMax(valued, parseRuleNumber(ruleArg))); break;
                case 'min': addError(validation.valueNumberMin(valued, parseRuleNumber(ruleArg))); break;
                case 'uint': addError(validation.valueNumberUInt(valued)); break;
                case 'int': addError(validation.valueNumberInt(valued)); break;
                case 'notonlynumbers': addError(validation.valueNotNumber(valued)); break;
                case 'length': addError(validation.valueLength(valued, parseRuleNumber(ruleArg))); break;
                case 'minlength': addError(validation.valueMinLength(valued, parseRuleNumber(ruleArg))); break;
                case 'maxlength': addError(validation.valueMaxLength(valued, parseRuleNumber(ruleArg))); break;
                case 'phone': addError(validation.valueIsPhone(valued)); break;
                case 'email': addError(validation.valueIsEmail(valued)); break;
                case 'same':
                    anotherControl = this._handlers.filter(i => i.wrapper.attr('name') === ruleArg)[0]?.disposables[0];
                    addSameError(anotherControl);
                    break;
                case 'samebyid':
                    anotherControl = this._handlers.filter(i => i.wrapper.attr('id') === ruleArg)[0]?.disposables[0];
                    addSameError(anotherControl);
                    break;
                default:
                    addError(validation.messages.unknownRule.replace("{arg0}", rule));
                    break;
            }
        }

        const uniqueErrors = [...new Set(validErrors)];
        return {
            isValid: uniqueErrors.length === 0,
            errorsText: uniqueErrors.join('\r\n'),
            hasRules: true
        };
    }

    private applyValidationResult(
        presentation: FieldValidationPresentation,
        result: FieldValidationResult
    ): void {
        presentation.fieldSet?.setCustomValidity(result.hasRules ? result.errorsText : '');
        const formItem = presentation.formItem;
        if (!formItem) return;

        formItem.removeClass(FormGS.TagClassValid);
        formItem.removeClass(FormGS.TagClassInvalid);
        const invalidWr = formItem.q('.invalid-feedback').first;
        if (invalidWr) {
            invalidWr.text = '';
            invalidWr.hide();
        }
        if (!result.hasRules) return;

        if (result.isValid) {
            formItem.addClass(FormGS.TagClassValid);
        } else {
            formItem.addClass(FormGS.TagClassInvalid);
            if (invalidWr && result.errorsText) {
                invalidWr.text = result.errorsText;
                invalidWr.show();
            }
        }
    }

    private unlinkControl(field: WrapperGS): void {
        const handler = this._handlers.find(control => control.wrapper === field);
        if (!handler) return;
        handler.disposables.forEach(disposable => disposable.dispose());
        array.remove(this._handlers, handler);
    }

    
    private addEventHandler()
    {        
        this.q('input, textarea, select').forEach(i => { this.linkControl(i); });
        this.q("[name]").forEach(i => { this.linkControl(i); });
    }

    private linkControl(wr: WrapperGS) {
        //check exists
        if (this._handlers.filter(l => l.wrapper === wr).length > 0) return;
        const handler = (e: Event) => {
            const wr = WrapperGS.Wrap(e.target);
            if (!wr) return;
            void this.validFieldAsync(wr).catch(error => {
                const identity = wr.attr('name') || wr.attr('id') || wr.el.tagName.toLowerCase();
                console.error(`GS form validation failed for field '${identity}'.`, error);
            });
        };
        this._handlers.push({
            wrapper: wr,
            disposables: [
                new EventGSHandlerDisposable(wr, "input", handler),
                new EventGSHandlerDisposable(wr, "change", handler)
            ]
        });
    }
}

function parseRuleNumber(value: string): number {
    const normalized = value.trim().replace(/,/g, '.');
    return normalized ? Number(normalized) : Number.NaN;
}

function isFileInputClearValue(value: unknown): boolean {
    return value === null
        || value === ''
        || (Array.isArray(value) && value.length === 0);
}

function getValidationValue(field: WrapperGS): Valued {
    const input = def.toType(field.el, HTMLInputElement);
    if (input?.type === 'file')
        return { value: Array.from(input.files ?? []) };
    return WrapperGS.ToValued(field) ?? new TextValueWrapper(field);
}

function getComparableValidationValue(field: WrapperGS): Valued | undefined {
    const input = def.toType(field.el, HTMLInputElement);
    if (input?.type === 'file')
        return { value: Array.from(input.files ?? []) };
    return WrapperGS.ToValued(field);
}

function inputIsUnselected(field: WrapperGS, controls: LinkedFormControl[]): boolean {
    const input = def.toType(field.el, HTMLInputElement);
    if (!input || (input.type !== 'checkbox' && input.type !== 'radio')) return false;

    const name = input.name;
    if (!name) return !input.checked;
    return !controls.some(control => {
        const candidate = def.toType(control.wrapper.el, HTMLInputElement);
        return candidate?.type === input.type && candidate.name === name && candidate.checked;
    });
}
WrapperGS.ExtendComponentsByTagClass(FormGS);
WrapperGS.ExtendComponentsByHTMLElement(FormGS);
